import { useEffect, useMemo, useState } from 'react';
import { Boxes, Gift, Info, Layers, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import {
  fetchAtomSource,
  fetchCanvasUiSource,
  fetchComponentOverview,
  fetchEarnFxSource,
  fetchPresentRuntime,
} from '../../api/modules/components';
import type { AtomMeta, CanvasUiComponentMeta, GalleryOverview, PresentComponentMeta } from '../../api/modules/components';
import { AtomPreview } from './AtomPreview';
import { EarnFxPreview } from './EarnFxPreview';
import { PresentPreview } from './PresentPreview';
import { CanvasUiPreview } from './CanvasUiPreview';

type Selection =
  | { kind: 'atom'; atom: AtomMeta }
  | { kind: 'present'; comp: PresentComponentMeta | null }
  | { kind: 'canvas'; comp: CanvasUiComponentMeta }
  | { kind: 'earnfx' };

const CATEGORY_ORDER = ['grid', 'layout', 'input', 'match', 'spawn', 'flow', 'fx', 'capability', 'system'];
const CATEGORY_LABEL: Record<string, string> = {
  grid: '网格布局',
  layout: '容器布局',
  input: '输入交互',
  match: '匹配判定',
  spawn: '生成补给',
  flow: '流程数值',
  fx: '特效反馈',
  capability: '设备能力',
  system: '系统支撑',
};

export function ComponentGallery() {
  const [overview, setOverview] = useState<GalleryOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState<Selection | null>(null);
  const [tab, setTab] = useState<'preview' | 'source'>('preview');
  const [sourceText, setSourceText] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);

  useEffect(() => {
    fetchComponentOverview()
      .then((ov) => {
        setOverview(ov);
        if (ov.atoms.length) setSel({ kind: 'atom', atom: ov.atoms[0] });
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        toast.error('组件注册表加载失败');
      });
  }, []);

  // 源码页签：按选中项拉取对应源码
  useEffect(() => {
    if (tab !== 'source' || !sel) return;
    let alive = true;
    setSourceLoading(true);
    const req =
      sel.kind === 'atom'
        ? fetchAtomSource(sel.atom.key).then((p) => p.source)
        : sel.kind === 'present'
          ? fetchPresentRuntime().then((p) => p.source)
          : sel.kind === 'canvas'
            ? fetchCanvasUiSource().then((p) => p.source)
            : fetchEarnFxSource().then((p) => p.source);
    req
      .then((src) => { if (alive) setSourceText(src); })
      .catch((e) => { if (alive) toast.error(e instanceof Error ? e.message : '源码加载失败'); })
      .finally(() => { if (alive) setSourceLoading(false); });
    return () => { alive = false; };
  }, [tab, sel]);

  const filteredAtoms = useMemo(() => {
    if (!overview) return [];
    const q = query.trim().toLowerCase();
    if (!q) return overview.atoms;
    return overview.atoms.filter((a) =>
      a.key.toLowerCase().includes(q)
      || a.className.toLowerCase().includes(q)
      || (a.description || '').toLowerCase().includes(q)
      || a.aliases.some((al) => al.toLowerCase().includes(q)),
    );
  }, [overview, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, AtomMeta[]>();
    CATEGORY_ORDER.forEach((c) => map.set(c, []));
    filteredAtoms.forEach((a) => {
      if (!map.has(a.category)) map.set(a.category, []);
      map.get(a.category)!.push(a);
    });
    return CATEGORY_ORDER.map((c) => ({ category: c, atoms: map.get(c) || [] })).filter((g) => g.atoms.length > 0);
  }, [filteredAtoms]);

  const presentList = useMemo(() => {
    if (!overview) return [];
    const q = query.trim().toLowerCase();
    if (!q) return overview.present;
    return overview.present.filter((p) =>
      p.id.toLowerCase().includes(q) || p.kind.toLowerCase().includes(q) || p.triggers.some((t) => t.toLowerCase().includes(q)),
    );
  }, [overview, query]);

  const selKey = sel ? (sel.kind === 'atom' ? `atom:${sel.atom.key}` : sel.kind === 'present' ? `present:${sel.comp?.id ?? '*'}` : sel.kind === 'canvas' ? `canvas:${sel.comp.id}` : 'earnfx') : '';

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden p-6">
      <div className="page-header flex items-center justify-between">
        <h2 className="page-title" style={{ animation: 'none' }}>组件图鉴</h2>
        {overview && (
          <div className="text-xs text-slate-500 font-mono">
            atoms {overview.atoms.length} · present {overview.present.length} · canvas-ui {overview.canvasUi.length} · earn-fx 1 · {overview.version}
          </div>
        )}
      </div>

      {error && (
        <div className="glass-card p-6 text-center text-sm text-rose-400">{error}（请确认后端 :3100 已启动）</div>
      )}

      {!overview && !error && (
        <div className="glass-card flex items-center justify-center gap-2 p-10 text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> 加载组件注册表…
        </div>
      )}

      {overview && (
        <>
          <div className="flex shrink-0 items-start gap-2 rounded-xl border border-cyan-400/15 bg-cyan-400/[0.04] px-3 py-2.5 text-[11px] leading-5 text-slate-400">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-300" />
            <div className="min-w-0">
              <span className="text-slate-300">图鉴演示 ≠ 生成时必然调用。</span>
              蓝图会装配蓝图规划引用的 atoms；模板仍由整套模板代码渲染，组件主要作为规划/配置元数据；微调直接修改最终产物源码。
              {overview.integration && (
                <span className="ml-1 font-mono text-slate-500">
                  当前蓝图规划 {overview.integration.blueprintPlanAtoms.length} · 模板规划 {overview.integration.templatePlanAtoms.length} · 未被玩法组合引用 {overview.integration.unreferencedAtoms.length}
                </span>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
          {/* 左栏：组件清单 */}
          <div className="glass-card flex min-h-0 w-[340px] shrink-0 flex-col overflow-hidden">
            <div className="border-b border-slate-700/50 p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索 key / 类名 / 描述 / 别名…"
                  className="input-neon w-full pl-8 pr-3 py-2 text-xs"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y p-2">
              {/* 机制原子 */}
              {grouped.map((g) => (
                <div key={g.category} className="mb-3">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-slate-500">
                    <Boxes className="h-3 w-3" />
                    {CATEGORY_LABEL[g.category] || g.category}
                    <span className="ml-auto font-mono text-slate-600">{g.atoms.length}</span>
                  </div>
                  {g.atoms.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setSel({ kind: 'atom', atom: a })}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                        selKey === `atom:${a.key}`
                          ? 'bg-cyan-400/15 text-cyan-200'
                          : 'text-slate-300 hover:bg-slate-800/60',
                      )}
                    >
                      <span className="font-mono">{a.key}</span>
                      {a.alwaysInclude && (
                        <span className="rounded bg-amber-400/15 px-1 text-[10px] text-amber-300">常驻</span>
                      )}
                      {a.integration?.blueprintPlan && (
                        <span className="rounded bg-cyan-400/10 px-1 text-[10px] text-cyan-300">蓝图</span>
                      )}
                      {a.integration?.templatePlan && (
                        <span className="rounded bg-violet-400/10 px-1 text-[10px] text-violet-300">模板规划</span>
                      )}
                      {a.integration && !a.integration.blueprintPlan && !a.integration.templatePlan && !a.integration.assemblerCapability && (
                        <span className="rounded bg-slate-700/50 px-1 text-[10px] text-slate-500">未接入</span>
                      )}
                      <span className="ml-auto truncate text-[10px] text-slate-600">{a.className}</span>
                    </button>
                  ))}
                </div>
              ))}

              {/* 呈现组件 */}
              {presentList.length > 0 ? (
                <div className="mb-3">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-slate-500">
                    <Layers className="h-3 w-3" />
                    呈现组件 present
                    <span className="ml-auto font-mono text-slate-600">{presentList.length}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSel({ kind: 'present', comp: null })}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                      selKey === 'present:*' ? 'bg-fuchsia-400/15 text-fuchsia-200' : 'text-slate-300 hover:bg-slate-800/60',
                    )}
                  >
                    <span className="font-mono">present-bus</span>
                    <span className="ml-auto text-[10px] text-slate-600">事件总线总览</span>
                  </button>
                  {presentList.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSel({ kind: 'present', comp: p })}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                        selKey === `present:${p.id}` ? 'bg-fuchsia-400/15 text-fuchsia-200' : 'text-slate-300 hover:bg-slate-800/60',
                      )}
                    >
                      <span className="font-mono">{p.id}</span>
                      <span className="ml-auto text-[10px] text-slate-600">{p.kind}</span>
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Canvas UI 表现组件 */}
              {overview.canvasUi.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-slate-500">
                    <Layers className="h-3 w-3" />
                    Canvas UI 基础组件
                    <span className="ml-auto font-mono text-slate-600">{overview.canvasUi.length}</span>
                  </div>
                  {overview.canvasUi.filter((c) => {
                    const q = query.trim().toLowerCase();
                    return !q || c.id.toLowerCase().includes(q) || c.kind.includes(q) || c.description.includes(q);
                  }).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSel({ kind: 'canvas', comp: c })}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                        selKey === `canvas:${c.id}` ? 'bg-emerald-400/15 text-emerald-200' : 'text-slate-300 hover:bg-slate-800/60',
                      )}
                    >
                      <span className="font-mono">{c.id}</span>
                      <span className="ml-auto text-[10px] text-slate-600">{c.kind}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Earn-FX 能力层 */}
              <div className="mb-3">
                <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-slate-500">
                  <Gift className="h-3 w-3" />
                  网赚能力层
                </div>
                <button
                  type="button"
                  onClick={() => setSel({ kind: 'earnfx' })}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                    selKey === 'earnfx' ? 'bg-amber-400/15 text-amber-200' : 'text-slate-300 hover:bg-slate-800/60',
                  )}
                >
                  <span className="font-mono">earn-fx</span>
                  <span className="rounded bg-red-400/15 px-1 text-[10px] text-red-300">红包</span>
                  <span className="ml-auto text-[10px] text-slate-600">8 钩子 · 4 样式</span>
                </button>
              </div>
            </div>
          </div>

          {/* 右栏：元数据 + 预览/源码 */}
          <div className="glass-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {sel && (
              <>
                <div className="flex items-center gap-3 border-b border-slate-700/50 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-slate-100">
                        {sel.kind === 'atom' ? sel.atom.className : sel.kind === 'present' ? (sel.comp?.id ?? 'present-bus') : sel.kind === 'canvas' ? sel.comp.id : 'earn-fx'}
                      </span>
                      {sel.kind === 'atom' && (
                        <span className="rounded bg-slate-700/60 px-1.5 py-0.5 text-[10px] text-slate-300">
                          {CATEGORY_LABEL[sel.atom.category] || sel.atom.category}
                        </span>
                      )}
                      {sel.kind === 'present' && sel.comp && (
                        <span className="rounded bg-fuchsia-400/15 px-1.5 py-0.5 text-[10px] text-fuchsia-300">
                          {sel.comp.kind} · 优先级 {sel.comp.priority}
                        </span>
                      )}
                      {sel.kind === 'canvas' && (
                        <span className="rounded bg-emerald-400/15 px-1.5 py-0.5 text-[10px] text-emerald-300">Canvas · 可装配</span>
                      )}
                    </div>
                    {sel.kind === 'atom' && sel.atom.description && (
                      <div className="mt-0.5 truncate text-xs text-slate-500">{sel.atom.description}</div>
                    )}
                    {sel.kind === 'canvas' && (
                      <div className="mt-0.5 truncate text-xs text-slate-500">{sel.comp.description}</div>
                    )}
                    {sel.kind === 'atom' && sel.atom.aliases.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {sel.atom.aliases.map((al) => (
                          <span key={al} className="rounded bg-slate-800 px-1.5 text-[10px] text-slate-500 font-mono">{al}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 rounded-lg border border-slate-700/60 p-0.5">
                    {(['preview', 'source'] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={cn(
                          'rounded-md px-3 py-1 text-xs transition-colors',
                          tab === t ? 'bg-cyan-400/20 text-cyan-200' : 'text-slate-400 hover:text-slate-200',
                        )}
                      >
                        {t === 'preview' ? '活预览' : '源码'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y p-4">
                  {tab === 'preview' ? (
                    sel.kind === 'atom' ? (
                      <AtomPreview key={sel.atom.key} atom={sel.atom} />
                    ) : sel.kind === 'present' ? (
                      <PresentPreview key={sel.comp?.id ?? 'bus'} component={sel.comp} />
                    ) : sel.kind === 'canvas' ? (
                      <CanvasUiPreview key={sel.comp.id} component={sel.comp} />
                    ) : (
                      <EarnFxPreview />
                    )
                  ) : sourceLoading ? (
                    <div className="flex items-center justify-center gap-2 p-10 text-slate-400 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" /> 加载源码…
                    </div>
                  ) : (
                    <pre className="overflow-auto rounded-lg bg-slate-950/70 p-4 text-[11px] leading-5 text-slate-300 font-mono whitespace-pre-wrap">
                      {sourceText}
                    </pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
